import { Delete as DeleteIcon, Description as DownloadFileIcon } from "@mui/icons-material";
import { Chip, Stack } from "@mui/material";
import { getDownloadURL, getStorage, ref } from "firebase/storage";
import groupBy from "lodash/groupBy";
import type { FC } from "react";
import { useState } from "react";
import ConfirmationDialog from "../confirmationDialog/ConfirmationDialog";

export interface AssociatedFile {
  readonly name: string;
  readonly url: string;
  readonly visibility?: "PRIVATE" | "PUBLIC";
}

interface FilesGroupProps {
  readonly values: (AssociatedFile & { idx: number })[];
  readonly onDownload: (url: string) => Promise<void>;
  readonly onRemove?: (idx: number, name: string) => void | Promise<void>;
}

const FilesGroup: FC<FilesGroupProps> = ({ values, onDownload, onRemove }) => (
  <Stack
    direction="row"
    spacing={1}
    sx={{
      flexWrap: "wrap",
    }}
  >
    {values?.map((file) => (
      <Chip
        key={`${file.name}:${file.url}`}
        label={file.name}
        variant="outlined"
        color={file.visibility === "PRIVATE" ? "error" : "primary"}
        onClick={(e) => {
          e.stopPropagation();
          return onDownload(file.url);
        }}
        onDelete={onRemove ? () => onRemove(file.idx, file.name) : undefined}
        icon={<DownloadFileIcon />}
        deleteIcon={<DeleteIcon />}
        data-testid="file-chip"
      />
    ))}
  </Stack>
);

interface Props {
  readonly values: AssociatedFile[] | undefined;
  readonly groupByVisibility?: boolean;
  readonly onRemove?: (idx: number) => void | Promise<void>;
}

export const downloadFile = async (url: string) => {
  if (url.startsWith("https://storage.googleapis.com/przezmorze")) {
    const storage = getStorage();
    const signedUrl = await getDownloadURL(ref(storage, url));
    window.open(signedUrl, "_blank");
  } else {
    window.open(url, "_blank");
  }
};

export const AssociatedFilesView: FC<Props> = ({ values, groupByVisibility, onRemove }) => {
  const [confirmationDialog, setConfirmationDialog] = useState<{
    content: string;
    onConfirm: () => Promise<void>;
  }>();

  const handleRemoveFile = async (idx: number, name: string) => {
    setConfirmationDialog({
      content: `Usunąć plik '${name}' ?`,
      onConfirm: async () => {
        await onRemove?.(idx);
        setConfirmationDialog(undefined);
      },
    });
  };

  if (!values?.length) {
    return null;
  }

  const indexedValues = values.map((v, idx) => ({ ...v, idx }));
  const groups = groupByVisibility ? groupBy(indexedValues, "visibility") : { all: indexedValues };

  return (
    <Stack direction="column" spacing={1}>
      {Object.entries(groups).map(([groupName, group]) => (
        <FilesGroup
          key={groupName}
          values={group}
          onDownload={downloadFile}
          onRemove={onRemove ? handleRemoveFile : undefined}
        />
      ))}
      {onRemove && (
        <ConfirmationDialog
          open={!!confirmationDialog}
          title="Usuń załącznik"
          content={confirmationDialog?.content}
          onCancel={() => setConfirmationDialog(undefined)}
          onConfirm={confirmationDialog?.onConfirm ?? (() => Promise.resolve())}
        />
      )}
    </Stack>
  );
};
